'use client'

import { useId, useState } from 'react'

import { Eyebrow, headingSection } from '@/components/marketing/brand/brand-ui'
import { cn } from '@/lib/utils'

import {
  BRANDED_APP_YEAR,
  CORE_MINIMUM_YEAR,
  CORE_PER_STUDENT_YEAR,
  estimateAnnual,
  usd,
  ONBOARDING_FEE
} from '../pricing-data'

function Toggle({
  checked,
  onChange,
  label,
  detail
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  detail: string
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-[#0F517B]/15 bg-white p-4 dark:border-white/10 dark:bg-slate-900">
      <span>
        <span className="block text-sm font-bold text-[#0F1B2D] dark:text-white">{label}</span>
        <span className="mt-0.5 block text-sm text-[#0F1B2D]/65 dark:text-slate-400">{detail}</span>
      </span>
      <input type="checkbox" className="peer sr-only" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span
        aria-hidden="true"
        className={cn(
          'relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#0F517B] dark:peer-focus-visible:outline-[#BAD7EC]',
          checked ? 'bg-[#0F517B]' : 'bg-[#0F1B2D]/20 dark:bg-white/20'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform',
            checked && 'translate-x-5'
          )}
        />
      </span>
    </label>
  )
}

export function PriceEstimator() {
  const sliderId = useId()
  const [students, setStudents] = useState(400)
  const [brandedApp, setBrandedApp] = useState(true)
  const estimate = estimateAnnual(students, brandedApp)

  const lines = [
    {
      label: estimate.atMinimum
        ? 'Core (annual minimum)'
        : `Core: ${students.toLocaleString('en-US')} students × $${CORE_PER_STUDENT_YEAR}`,
      value: estimate.core
    },
    ...(brandedApp ? [{ label: 'Branded app', value: estimate.branded }] : [])
  ]

  return (
    <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
      <div>
        <Eyebrow className="mb-3">Estimate your cost</Eyebrow>
        <h2 className={headingSection}>What would it cost us?</h2>
        <p className="mt-4 text-lg leading-relaxed text-[#0F1B2D]/75 dark:text-slate-300">
          Move the slider to your enrollment. Switch off the branded app to see Core on its own.
        </p>

        <div className="mt-8">
          <div className="flex items-baseline justify-between">
            <label htmlFor={sliderId} className="text-sm font-bold text-[#0F1B2D] dark:text-white">
              Students
            </label>
            <span className="font-heading text-3xl text-[#0F517B] dark:text-white">
              {students.toLocaleString('en-US')}
            </span>
          </div>
          <input
            id={sliderId}
            type="range"
            min={50}
            max={2000}
            step={10}
            value={students}
            onChange={e => setStudents(Number(e.target.value))}
            className="mt-3 w-full accent-[#0F517B]"
          />
          <div className="mt-1 flex justify-between text-xs text-[#0F1B2D]/70 dark:text-slate-400">
            <span>50</span>
            <span>2,000</span>
          </div>
        </div>

        <div className="mt-6 max-w-sm">
          <Toggle
            checked={brandedApp}
            onChange={setBrandedApp}
            label="Branded app"
            detail={`+${usd(BRANDED_APP_YEAR)} per year`}
          />
        </div>
      </div>

      <div className="rounded-xl bg-[#F8FAFC] p-8 ring-1 ring-[#0F517B]/10 dark:bg-slate-900 dark:ring-white/10">
        <p className="text-xs font-bold tracking-[0.2em] text-[#1D6693] uppercase dark:text-[#BAD7EC]">
          Estimated per year
        </p>
        <p className="mt-3 font-heading text-6xl tracking-tight text-[#0F517B] dark:text-white" aria-live="polite">
          {usd(estimate.total)}
        </p>
        <dl className="mt-6 space-y-2 border-t border-[#0F517B]/10 pt-5 text-sm dark:border-white/10">
          {lines.map(line => (
            <div key={line.label} className="flex justify-between gap-4">
              <dt className="text-[#0F1B2D]/70 dark:text-slate-300">{line.label}</dt>
              <dd className="font-semibold text-[#0F1B2D] dark:text-white">{usd(line.value)}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-4 flex justify-between gap-4 border-t border-dashed border-[#0F517B]/15 pt-4 text-sm dark:border-white/15">
          <span className="text-[#0F1B2D]/70 dark:text-slate-300">Plus one-time onboarding, first year only</span>
          <span className="font-semibold text-[#0F1B2D] dark:text-white">{usd(ONBOARDING_FEE)}</span>
        </div>
        <p className="mt-6 text-xs leading-relaxed text-[#0F1B2D]/70 dark:text-slate-400">
          An estimate based on annual billing, not a quote. Core has a {usd(CORE_MINIMUM_YEAR)} yearly minimum. Custom
          work is quoted separately.
        </p>
      </div>
    </div>
  )
}
